/**
 * Dashboard — HX Design Language (FROZEN, official). Consumes the shared
 * HX design system (@/hx) — no page-specific CSS. All endpoints,
 * permissions and data-testids preserved.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCall, formatDuration } from "@/context/CallContext";
import { useCommandPalette } from "@/components/CommandPalette";
import OnboardingCallout from "@/components/OnboardingCallout";
import {
  MagnifyingGlass, Phone, PhoneIncoming, PhoneOutgoing, PhoneX,
  ChatText, AddressBook, ListBullets, TrendUp, Clock,
  Microphone, ArrowUpRight, CaretRight, User, UsersThree, Lightning,
} from "@phosphor-icons/react";
import { HxCard, HxCardHead, HxButton, HxKpi, HxChip, HxTile, HxEmpty, HxSkeleton, HxAreaChart, HX_COLORS as C } from "@/hx";

export default function Dashboard() {
  const { user } = useAuth();
  const { startCall } = useCall();
  const { open: openPalette } = useCommandPalette();
  const [overview, setOverview] = useState(null);
  const [timeline14, setTimeline14] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const canSeeTeam = ["admin", "supervisor"].includes(user?.role) && user?.organization?.type !== "individual";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a7, a14, c, ct, tm] = await Promise.all([
          api.get("/analytics/overview?days=7"),
          api.get("/analytics/overview?days=14"),
          api.get("/calls?limit=6"),
          api.get("/contacts"),
          canSeeTeam ? api.get("/team").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;
        setOverview(a7.data);
        setTimeline14(a14.data?.timeline || []);
        setRecentCalls(c.data || []);
        setContacts((ct.data || []).slice(0, 4));
        setTeam((tm.data || []).filter((m) => m.role !== "owner").slice(0, 4));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [canSeeTeam]);

  const t = overview?.totals || {};
  const days = fillDays(timeline14, 14);
  const thisWeek = days.slice(7), lastWeek = days.slice(0, 7);
  const sum = (arr, k) => arr.reduce((s, d) => s + (k === "total" ? d.inbound + d.outbound + d.missed : d[k]), 0);
  const deltas = {
    total:   pct(sum(thisWeek, "total"),   sum(lastWeek, "total")),
    inbound: pct(sum(thisWeek, "inbound"), sum(lastWeek, "inbound")),
    missed:  pct(sum(thisWeek, "missed"),  sum(lastWeek, "missed")),
  };
  const answerRateSeries = thisWeek.map((d) => {
    const tot = d.inbound + d.outbound + d.missed;
    return tot ? Math.round(((tot - d.missed) / tot) * 100) : 0;
  });

  const firstName = (user?.name || "").split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="hbf-hx" data-testid="dashboard-page">
      <OnboardingCallout />

      {/* ── Command bar ─────────────────────────────────────── */}
      <div className="hx-cmdbar hx-up">
        {user?.role === "admin" ? (
          <button className="hx-search" onClick={openPalette} data-testid="dashboard-search">
            <MagnifyingGlass size={16} weight="bold" />
            Search anything…
            <kbd>⌘K</kbd>
          </button>
        ) : null}
        <div style={{ flex: 1 }} />
        <div className="hx-userchip" data-testid="dashboard-userchip">
          <img src={user?.avatar_url} alt="" />
          <div>
            <div className="hx-userchip__name">{user?.name}</div>
            <div className="hx-userchip__role">{user?.role} · {user?.organization?.name || "Workspace"}</div>
          </div>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="hx-hero hx-up hx-d1" data-testid="dashboard-hero">
        <div>
          <div className="hx-hero__eyebrow" data-testid="dashboard-eyebrow">
            {greeting}, {firstName} <span aria-hidden>👋</span>
          </div>
          <h1 className="hx-hero__title" data-testid="dashboard-title">
            Welcome back to Hello <em>by Flow</em>
          </h1>
          <p className="hx-hero__sub" data-testid="dashboard-subtitle">
            Everything happening in your business today — {today.toLowerCase()}.
          </p>
        </div>
        <div className="hx-hero__cta" data-testid="dashboard-quick-actions">
          <HxButton to="/app/sms" data-testid="dashboard-open-messages">
            <ChatText size={16} weight="duotone" /> Send a message
          </HxButton>
          <HxButton to="/app/dialer" variant="primary" data-testid="dashboard-open-dialer">
            <Phone size={16} weight="fill" /> Make a call
          </HxButton>
        </div>
      </header>

      {/* ── KPI band ─────────────────────────────────────────── */}
      <div className="hx-grid-kpi hx-up hx-d2" data-testid="dashboard-kpi-row">
        <HxKpi tone="blue"   icon={Phone}         label="Total calls" value={num(t.total_calls)} delta={deltas.total}   series={thisWeek.map(d => d.inbound + d.outbound + d.missed)} color={C.blue}   testid="kpi-total-calls" />
        <HxKpi tone="green"  icon={PhoneIncoming} label="Inbound"     value={num(t.inbound)}     delta={deltas.inbound} series={thisWeek.map(d => d.inbound)} color={C.green}  testid="kpi-inbound" />
        <HxKpi tone="red"    icon={PhoneX}        label="Missed"      value={num(t.missed)}      delta={deltas.missed} invert series={thisWeek.map(d => d.missed)} color={C.red} testid="kpi-missed" />
        <HxKpi tone="violet" icon={TrendUp}       label="Answer rate" value={t.answer_rate != null ? Math.round(t.answer_rate) : "—"} suffix="%" series={answerRateSeries} color={C.violet} testid="kpi-answer-rate" />
      </div>

      {/* ── Volume chart + unified right rail ────────────────── */}
      <div className="hx-grid-main hx-up hx-d3" style={{ marginBottom: 20 }}>
        <HxCard className="hx-span7" data-testid="call-volume-card">
          <HxCardHead title="Call volume overview" right={<span style={{ fontSize: 12, fontWeight: 700, color: "var(--hx-ink-3)" }}>Last 7 days</span>} />
          <div className="hx-chart">
            <HxAreaChart days={thisWeek} />
          </div>
          <div className="hx-legend">
            <LegendStat dot={C.blue}   label="Total"    value={sum(thisWeek, "total")} />
            <LegendStat dot={C.green}  label="Inbound"  value={sum(thisWeek, "inbound")} />
            <LegendStat dot={C.violet} label="Outbound" value={sum(thisWeek, "outbound")} />
            <LegendStat dot={C.red}    label="Missed"   value={sum(thisWeek, "missed")} />
          </div>
        </HxCard>

        <div className="hx-span5 hx-rail">
          <HxCard data-testid="quick-actions-card">
            <HxCardHead title="Quick actions" icon={<Lightning size={15} weight="fill" color={C.orange} />} />
            <div style={{ padding: "6px 10px 12px" }}>
              <QA to="/app/dialer"   tone="blue"   icon={Phone}       label="Make a call"    testid="qa-dialer" />
              <QA to="/app/sms"      tone="green"  icon={ChatText}    label="Send a message" testid="qa-messages" />
              <QA to="/app/contacts" tone="violet" icon={AddressBook} label="Add a contact"  testid="dashboard-open-contacts" />
              <QA to="/app/calls"    tone="orange" icon={ListBullets} label="View call logs" testid="dashboard-open-calls" />
            </div>
          </HxCard>

          <HxCard data-testid="dashboard-metrics-row">
            <HxCardHead title="This week's snapshot" />
            <div style={{ padding: "4px 0 12px" }}>
              <Snap icon={Clock}         label="Total talk time"   value={`${t.total_minutes ?? 0} min`} testid="metric-total-minutes" />
              <Snap icon={Clock}         label="Avg call duration" value={t.avg_duration ? formatDuration(Math.round(t.avg_duration)) : "—"} testid="metric-avg-duration" />
              <Snap icon={PhoneOutgoing} label="Outbound calls"    value={num(t.outbound) ?? "—"} testid="metric-outbound" />
              <Snap icon={Microphone}    label="Recordings"        value={recentCalls.filter((c) => c.recorded).length} testid="metric-recordings" />
            </div>
          </HxCard>
        </div>
      </div>

      {/* ── Activity + quick dial / team ─────────────────────── */}
      <div className="hx-grid-main hx-up hx-d4">
        <HxCard className="hx-span7" data-testid="recent-calls-card">
          <HxCardHead
            title="Recent activity"
            right={<Link to="/app/calls" className="hx-card__link" data-testid="dashboard-view-all-calls">View all <ArrowUpRight size={13} weight="bold" /></Link>}
          />
          {loading && recentCalls.length === 0 ? (
            <HxSkeleton height={48} count={4} style={{ padding: "8px 24px 20px" }} />
          ) : recentCalls.length === 0 ? (
            <HxEmpty icon={<Phone size={22} weight="duotone" />} data-testid="recent-calls-empty">
              No calls yet — make your first one!
            </HxEmpty>
          ) : (
            <div className="hx-feed">
              {recentCalls.map((c, i) => <FeedRow key={c.id} c={c} index={i} />)}
            </div>
          )}
        </HxCard>

        <div className="hx-span5 hx-rail">
          <HxCard data-testid="quick-dial-card">
            <HxCardHead
              title="Quick dial"
              right={<Link to="/app/contacts" className="hx-card__link">All contacts <ArrowUpRight size={13} weight="bold" /></Link>}
            />
            {loading && contacts.length === 0 ? (
              <HxSkeleton height={46} count={3} style={{ padding: "8px 20px 18px" }} />
            ) : contacts.length === 0 ? (
              <HxEmpty icon={<User size={22} weight="duotone" />} data-testid="quick-dial-empty">
                No contacts yet — add someone from Contacts.
              </HxEmpty>
            ) : (
              <div style={{ padding: "2px 10px 10px" }}>
                {contacts.map((c) => (
                  <div key={c.id} className="hx-dial" data-testid={`quick-dial-${c.id}`}>
                    <div className="hx-feed__avatar" style={{ background: avatarGradient(c.name) }}>
                      {c.avatar_url ? <img src={c.avatar_url} alt="" style={{ width: "100%", height: "100%", borderRadius: 12, objectFit: "cover" }} /> : initials(c.name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="hx-feed__who" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
                      <div className="hx-feed__what">{c.phone}</div>
                    </div>
                    <button className="hx-dial__call" onClick={() => startCall({ to_number: c.phone, to_name: c.name })} data-testid={`quick-dial-call-${c.id}`}>
                      <Phone size={12} weight="fill" /> Call
                    </button>
                  </div>
                ))}
              </div>
            )}
          </HxCard>

          {canSeeTeam && team.length > 0 && (
            <HxCard data-testid="team-pulse-card">
              <HxCardHead
                title="Team pulse"
                icon={<UsersThree size={15} weight="duotone" color={C.violet} />}
                right={<Link to="/app/leaderboard" className="hx-card__link">Leaderboard <ArrowUpRight size={13} weight="bold" /></Link>}
              />
              <div style={{ padding: "2px 10px 10px" }}>
                {team.map((m) => (
                  <div key={m.id} className="hx-dial" data-testid={`team-pulse-${m.id}`}>
                    {m.avatar_url
                      ? <img src={m.avatar_url} alt="" style={{ width: 38, height: 38, borderRadius: 12 }} />
                      : <div className="hx-feed__avatar" style={{ background: avatarGradient(m.name) }}>{initials(m.name)}</div>}
                    <div style={{ minWidth: 0 }}>
                      <div className="hx-feed__who" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
                      <div className="hx-feed__what" style={{ textTransform: "capitalize" }}>{m.role}</div>
                    </div>
                    <div style={{ marginLeft: "auto", textAlign: "right" }}>
                      <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em" }}>{m.calls_today ?? 0}</div>
                      <div style={{ fontSize: 10.5, color: "var(--hx-ink-3)", fontWeight: 700 }}>calls today</div>
                    </div>
                  </div>
                ))}
              </div>
            </HxCard>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Page pieces ────────────────────────────────────────────── */

function LegendStat({ dot, label, value }) {
  return (
    <div className="hx-legend__item">
      <div className="hx-legend__key"><span className="hx-legend__dot" style={{ background: dot }} /> {label}</div>
      <div className="hx-legend__val">{value.toLocaleString()}</div>
    </div>
  );
}

function QA({ to, tone, icon: Icon, label, testid }) {
  return (
    <Link to={to} className="hx-qa-row" data-testid={testid}>
      <HxTile tone={tone} size={36} radius={11} className="hx-qa-ic"><Icon size={17} weight="duotone" /></HxTile>
      {label}
      <CaretRight size={14} weight="bold" className="hx-qa-chev" />
    </Link>
  );
}

function Snap({ icon: Icon, label, value, testid }) {
  return (
    <div className="hx-snap" data-testid={testid}>
      <span className="hx-snap__l"><Icon size={15} weight="duotone" color={C.blue} /> {label}</span>
      <span className="hx-snap__v" data-testid={testid ? `${testid}-value` : undefined}>{value}</span>
    </div>
  );
}

function FeedRow({ c, index = 0 }) {
  const what = c.direction === "inbound" ? "Incoming call" : "Outgoing call";
  return (
    <div className="hx-feed__row" style={{ animationDelay: `${index * 0.05}s` }} data-testid={`recent-call-${c.id}`}>
      <div className="hx-feed__avatar" style={{ background: avatarGradient(c.to_name) }}>{initials(c.to_name)}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="hx-feed__who">{c.to_name || "Unknown"}</div>
        <div className="hx-feed__what">{what} · {c.to_number}</div>
      </div>
      <StatusPill s={c.status} />
      <div className="hx-feed__time" style={{ textAlign: "right" }}>
        <div style={{ fontWeight: 700, color: "#475569" }}>{c.duration ? formatDuration(c.duration) : "—"}</div>
        <div>{timeAgo(c.started_at)}</div>
      </div>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────── */
function num(v) { return typeof v === "number" ? v : v == null ? "—" : v; }
function pct(cur, prev) { if (!prev) return null; return Math.round(((cur - prev) / prev) * 100); }
function initials(name) { return (name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase(); }

const GRADS = [
  "linear-gradient(135deg,#2E6BF0,#7C3AED)", "linear-gradient(135deg,#059669,#2563EB)",
  "linear-gradient(135deg,#EA580C,#DC2626)", "linear-gradient(135deg,#7C3AED,#DB2777)",
  "linear-gradient(135deg,#0EA5E9,#059669)",
];
function avatarGradient(name) {
  let hsh = 0;
  for (const ch of (name || "?")) hsh = (hsh * 31 + ch.charCodeAt(0)) >>> 0;
  return GRADS[hsh % GRADS.length];
}

function fillDays(timeline, n) {
  const map = Object.fromEntries((timeline || []).map((d) => [d.day, d]));
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const dt = new Date(Date.now() - i * 86400000);
    const key = dt.toISOString().slice(0, 10);
    out.push(map[key] || { day: key, inbound: 0, outbound: 0, missed: 0 });
  }
  return out;
}

export function DirectionPill({ d }) {
  return (
    <HxChip tone={d === "inbound" ? "blue" : "gray"} data-testid={`direction-${d}`}>
      {d === "inbound" ? <PhoneIncoming size={10} weight="bold" /> : <PhoneOutgoing size={10} weight="bold" />}
      {d}
    </HxChip>
  );
}
export function StatusPill({ s }) {
  const tone = s === "completed" ? "green" : s === "missed" ? "red" : s === "in-progress" ? "blue" : "amber";
  return <HxChip tone={tone} data-testid={`status-${s}`}>{s}</HxChip>;
}

export function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
